import { useModalStore } from "@/hooks/Modal/hooks";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { Form, Modal, Select } from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect } from "react";
import OrderSyncType from "./OrderSyncType";
import SyncOrderProcessModal from "./SyncOrderProcessModal";
import ShopType from "@/enum/ShopType";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";

interface SupplierIncrementSyncOrderModalProps extends InjectBaseModalProps {
    supplierShopId: number;

    tab: OrderTab;
}

/** 代理商手工店铺无需同步订单 */
export const hideSyncRetailerShopTypes = [ShopType.MANUAL, ShopType.INDEPENDENT_MANUAL];

const SupplierIncrementSyncOrderModal: React.FC<SupplierIncrementSyncOrderModalProps> = function (props) {
    const { open, onCancel, tab, supplierShopId } = props;
    const [form] = Form.useForm();
    const { supplierPartnershipsStore } = useOrderManagementStore();
    const partnerships = supplierPartnershipsStore.supplierPartnershipsMap.get(supplierShopId);

    const { openModal } = useModalStore();

    async function handleSyncClick() {
        const { retailerShopId } = await form.validateFields();

        const partnership = supplierPartnershipsStore.retailerPartnershipMap.get(retailerShopId);

        if (!partnership) {
            Modal.error({ title: "无法找到对应的代理商，请联系客服反馈" });

            return;
        }

        openModal(SyncOrderProcessModal, {
            supplierShopId,
            tab,
            orderSyncType: OrderSyncType.INCREMENT,
            partnership,
        });

        onCancel();
    }

    const cooperativePartnerships = (partnerships || []).filter(({ suspended }) => !suspended);

    useEffect(() => {
        supplierPartnershipsStore.ensureEstablishedPartnerships(supplierShopId);
    }, []);

    useEffect(() => {
        if (!cooperativePartnerships?.length) return;

        const partnership = cooperativePartnerships.find((r) => !hideSyncRetailerShopTypes.includes(r.shopType));

        if (!partnership) return;

        form.setFieldsValue({ retailerShopId: partnership.shopId });
    }, [partnerships]);

    const loading = supplierPartnershipsStore.supplierPartnershipsLoadingMap.get(supplierShopId);

    const options = [];

    cooperativePartnerships.forEach((partnership) => {
        if (hideSyncRetailerShopTypes.includes(partnership.shopType)) return;

        options.push({
            key: partnership.shopId,
            value: partnership.shopId,
            label: partnership.alias || partnership.name,
        });
    });

    return (
        <Modal title="同步代理商订单" open={open} onCancel={onCancel} onOk={handleSyncClick}>
            <Form form={form}>
                <Form.Item name="retailerShopId" label="代理商名称" rules={[{ required: true }]}>
                    <Select
                        loading={loading}
                        showSearch={{
                            optionFilterProp: "label",
                        }}
                        options={options}
                    />
                </Form.Item>

                <Form.Item shouldUpdate label="店铺类型" labelCol={{ xs: 5 }}>
                    {() => {
                        const { retailerShopId } = form.getFieldsValue();

                        if (!retailerShopId) return null;

                        const partnership = supplierPartnershipsStore.retailerPartnershipMap.get(retailerShopId);

                        if (!partnership) return null;

                        return <span>{ShopTypeNameMap[partnership.shopType]}</span>;
                    }}
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default observer(SupplierIncrementSyncOrderModal);
